/**
 * Auth context — phone OTP authentication state.
 * Context value is useMemo'd and callbacks useCallback'd so useAuth()
 * consumers re-render only when auth state actually changes — keep it
 * that way (ChatWidget + ProtectedRoute consume this on hot paths).
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import authService, { UserProfile, TokenResponse } from '../services/authService';

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isNewUser: boolean;
}

interface AuthContextType extends AuthState {
  requestOTP: (phone: string) => Promise<void>;
  verifyOTP: (phone: string, code: string) => Promise<TokenResponse>;
  register: (data: { first_name: string; last_name: string; father_name?: string; email?: string; phone?: string; industry?: string; password?: string }) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: authService.getStoredUser(),
    isAuthenticated: authService.isAuthenticated(),
    isLoading: true,
    isNewUser: false,
  });

  // On mount: check if token is valid
  useEffect(() => {
    const init = async () => {
      if (authService.isAuthenticated()) {
        try {
          const profile = await authService.getProfile();
          setState(s => ({ ...s, user: profile, isAuthenticated: true, isLoading: false }));
        } catch (err) {
          // Token expired or invalid — log out silently. UI will redirect to
          // login via ProtectedRoute. Logged for observability so we notice
          // if /users/me regresses unexpectedly.
          console.warn('[AuthContext] profile fetch failed; logging out', err);
          authService.logout();
          setState(s => ({ ...s, user: null, isAuthenticated: false, isLoading: false }));
        }
      } else {
        setState(s => ({ ...s, isLoading: false }));
      }
    };
    init();
  }, []);

  const requestOTP = useCallback(async (phone: string) => {
    await authService.requestOTP(phone);
  }, []);

  const verifyOTP = useCallback(async (identifier: string, code: string) => {
    // Detect if identifier is email or phone
    const isEmail = identifier.includes('@');
    const result = isEmail
      ? await authService.verifyOTPByEmail(identifier, code)
      : await authService.verifyOTP(identifier, code);
    const profile = await authService.getProfile();
    setState(s => ({
      ...s,
      user: profile,
      isAuthenticated: true,
      isNewUser: result.is_new_user,
    }));
    return result;
  }, []);

  const register = useCallback(async (data: { first_name: string; last_name: string; father_name?: string; email?: string; phone?: string; industry?: string; password?: string }) => {
    await authService.register(data);
    const profile = await authService.getProfile();
    setState(s => ({ ...s, user: profile, isNewUser: false }));
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setState({ user: null, isAuthenticated: false, isLoading: false, isNewUser: false });
  }, []);

  const refreshUser = useCallback(async () => {
    const profile = await authService.getProfile();
    setState(s => ({ ...s, user: profile }));
  }, []);

  // Memoized so consumers only re-render when `state` changes.
  const value = useMemo<AuthContextType>(
    () => ({ ...state, requestOTP, verifyOTP, register, logout, refreshUser }),
    [state, requestOTP, verifyOTP, register, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
